// Copyright (c) Docugami, Inc. All rights reserved.

import { ITextField, ITextFieldProps, Stack, TextField } from "@fluentui/react";
import { DgButton } from "components/common/DgCommonStyledComponents";
import { dgFontFamily } from "components/common/Fonts";
import { homeGray, primaryTheme } from "components/shell/presentation/AppStyles";
import { useRef, useState } from "react";
import { ChatSuggestion } from "store/chat/chatSlice";
import styled from "styled-components";

const CHAT_INPUT_HEIGHT = 100;

const Root = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    position: relative;
    margin-top: auto;
`;

interface ChatInputFieldProps {
    docSetId: string;
    onSend: (text: string) => void;
    suggestions?: ChatSuggestion[];
    onSendSuggestion?: (suggestionId: string) => void;
    disabled?: boolean;
}

export const ChatInputField = (props: ChatInputFieldProps): JSX.Element => {
    const [text, setText] = useState('');

    const ref = useRef<ITextField>(null);

    function onSend() {
        if (!!text.trim()) {
            props.onSend(text);
            setText("");
        }
    }

    function onInputKeyDown(ev: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
        if (ev.key === 'Enter' && !ev.shiftKey) {
            onSend();
            ev.stopPropagation();
        }
    }

    function onChange(ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) {
        // Note this doesn't allow intiial newlines
        if (!text && !newValue?.trim()) {
            return;
        }
        setText(newValue ?? '');
    }

    function onRenderSuffix(props?: ITextFieldProps, defaultRender?: (props?: ITextFieldProps) => JSX.Element | null) {
        return (
            <DgButton
                title="Ask question"
                disabled={!text.trim()}
                onClick={onSend}
                style={{ height: `${CHAT_INPUT_HEIGHT * 0.8}px` }} // 80% of input height
            >
                ASK
            </DgButton>
        );
    }

    return (
        <Root>
            { !!props.suggestions?.length && <SuggestedMessagesList messages={props.suggestions} onClick={props.onSendSuggestion} disabled={props.disabled} /> }
            <Stack
                horizontal
                tokens={{ childrenGap: '10px' }}
            >
                <Stack.Item styles={{ root: { flex: 1 } }}>
                    <TextField
                        componentRef={ref}
                        placeholder="Ask me about something in your doc set..."
                        value={text}
                        onChange={onChange}
                        onKeyDown={onInputKeyDown}
                        styles={{
                            fieldGroup: { alignItems: 'center', height: `${CHAT_INPUT_HEIGHT}px` },
                            field: { height: '100%', minHeight: 0, display: 'flex', alignItems: 'center' },
                            suffix: { background: 'none' }
                        }}
                        autoFocus
                        multiline
                        borderless
                        disabled={props.disabled}
                        resizable={false}
                        suffix="ask"
                        onRenderSuffix={onRenderSuffix}
                    />
                </Stack.Item>
            </Stack>
        </Root>
    )
}

const SuggestedMessageBubble = styled.div<{disabled?: boolean}>`
    font-family: ${dgFontFamily};
    font-size: 14px;
    font-weight: 500;
    line-height: 17px;
    text-align: center;
    color: ${homeGray};

    background: ${primaryTheme.palette.white};
    box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.2);
    border-radius: 2px;
    padding: 5px;
    box-sizing: border-box;
    cursor: pointer;

    visibility: visible;
    opacity: 1;
    ${props => !!props.disabled && 'visibility: hidden; opacity: 0'};
    transition: visibility 0.2s, opacity 0.2s linear;

    :hover {
        transform: scale(1.02);
    }
`;

const ListRoot = styled.div`
    display: flex;
    flex-direction: row;
    gap: 15px;
    align-self: flex-end;
    padding: 10px;
    box-sizing: border-box;
`;

interface SuggestedMessagesListProps {
    messages: ChatSuggestion[];
    onClick?: (id: string) => void;
    disabled?: boolean;
}

const SuggestedMessagesList = (props: SuggestedMessagesListProps): JSX.Element => {
    return (
        <ListRoot>
            { props.messages.slice(0, 3).map(m => <SuggestedMessageBubble key={m.id} disabled={props.disabled} onClick={() => props.onClick?.(m.id)}>{m.content}</SuggestedMessageBubble>)}
        </ListRoot>
    )
}
